import type { LunarCalendarYearDataEncoded } from 'owa-date-lunar-calendar-data';
import { lazyGetLunarCalendarYearDataEncoded } from 'owa-date-lunar-calendar-data';
import { LunarCalendarType } from './LunarCalendarType';

export interface LunarCalendarYearData {
    /**
     * @description Gregorian month of the lunar year
     */
    gregorianMonthOfLunarNewYear: number; // Gregorian month of the lunar new year

    /**
     * @description Gregorian day of the lunar year
     */
    gregorianDayOfLunarNewYear: number; // Gregorian day of the lunar new year

    /**
     * @description Array of the number of days - either 29 or 30 - in each month
     */
    numberOfDaysInMonth: number[]; // Array of the number of days - either 29 or 30 - in each month

    /**
     * @description If the year contains a leap month, index of that month
     */
    leapMonth?: number; // If the year contains a leap month, index of that month
}

export default function getLunarCalendarYearData(
    gregorianYear: number,
    lunarCalendarType: LunarCalendarType
): LunarCalendarYearData | undefined {
    const getLunarCalendarYearDataEncoded = lazyGetLunarCalendarYearDataEncoded.tryImportForRender();

    if (!getLunarCalendarYearDataEncoded) {
        return undefined;
    }

    const dataEncoded = getLunarCalendarYearDataEncoded(gregorianYear);

    if (!dataEncoded) {
        return undefined;
    }

    return {
        gregorianMonthOfLunarNewYear: dataEncoded.monthOfLunarNewYear,
        gregorianDayOfLunarNewYear: getGregorianDayOfLunarNewYear(dataEncoded, lunarCalendarType),
        numberOfDaysInMonth: getNumberOfDaysInMonth(dataEncoded, lunarCalendarType),
        leapMonth: getLeapMonth(dataEncoded, lunarCalendarType),
    };
}

function getGregorianDayOfLunarNewYear(
    dataEncoded: LunarCalendarYearDataEncoded,
    lunarCalendarType: LunarCalendarType
): number {
    if (lunarCalendarType === LunarCalendarType.Chinese && dataEncoded.dayOfLunarNewYearChinese) {
        return dataEncoded.dayOfLunarNewYearChinese;
    }

    return dataEncoded.dayOfLunarNewYear;
}

function getNumberOfDaysInMonth(
    dataEncoded: LunarCalendarYearDataEncoded,
    lunarCalendarType: LunarCalendarType
): number[] {
    const daysInMonthEncoded =
        lunarCalendarType === LunarCalendarType.Chinese &&
        dataEncoded.numberOfDaysInMonthEncodedChinese
            ? dataEncoded.numberOfDaysInMonthEncodedChinese
            : dataEncoded.numberOfDaysInMonthEncoded;

    const isLeapYear = getLeapMonth(dataEncoded, lunarCalendarType) !== undefined;

    return decodeDaysInMonth(daysInMonthEncoded, isLeapYear);
}

function getLeapMonth(
    dataEncoded: LunarCalendarYearDataEncoded,
    lunarCalendarType: LunarCalendarType
): number | undefined {
    if (lunarCalendarType === LunarCalendarType.Chinese && dataEncoded.leapMonthChinese) {
        return dataEncoded.leapMonthChinese;
    }

    return dataEncoded.leapMonth;
}

function decodeDaysInMonth(encodedDays: number, isLeapYear: boolean): number[] {
    const numberOfMonths = isLeapYear ? 13 : 12;
    let result: number[] = [];

    for (let month = 0; month < numberOfMonths; month++) {
        const mask = 1 << month;

        // If the bit is set, there are 30 days in the month, otherwise 29
        if (encodedDays & mask) {
            result.push(30);
        } else {
            result.push(29);
        }
    }

    return result;
}
